/**
 * Responsibility: Implement dialog openers behavior for the ui/dialog surface.
 * Scope: Owns ui/dialog concerns only; cross-surface orchestration is excluded.
 */

import { decorateObjectName } from '../../engine/object-emoji'
import { getItemActions, getNpcActions } from '../../engine/permissions'
import type { GameDialogOptions, GameDialogShellOptions, NPC } from "@anyquest/shared-core"
import { dispatcher } from '../../app-states'
import { effectChipText } from '../helpers/effect-chip'
import { getItemStatusInfo } from '../helpers/item-status'
import { normalizeNpcSheet } from '../helpers/sheet-contracts'
import * as gameStore from '../../game-store'
import { TOPICS } from "@anyquest/shared-core"

type ItemDialogConfig = {
  desc?: string
  meta?: string
  location?: 'inventory' | 'room'
  equipped?: boolean
}

type EntityContextData = {
  media?: Record<string, unknown>
  memories?: string[]
  npcs?: Array<Record<string, unknown>>
  items?: string[]
  quests?: Array<Record<string, unknown>>
  allowEntityInteraction?: boolean
  onOpenNpc?: (npc: unknown) => void
  onOpenItem?: (itemName: string) => void
}

type InfoDialogConfig = {
  data?: EntityContextData
  options?: GameDialogShellOptions
}

function relationLabel(rel: number) {
  if (rel >= 80) return 'DEVOTED'
  if (rel >= 50) return 'FRIENDLY'
  if (rel >= 20) return 'NEUTRAL'
  if (rel >= -20) return 'WARY'
  if (rel >= -50) return 'HOSTILE'
  return 'ENEMY'
}

function relationTone(rel: number) {
  if (rel >= 40) return 'friendly'
  if (rel <= -40) return 'hostile'
  return 'neutral'
}

function activeNpcEffects(npc: NPC) {
  return [...new Set((npc?.activeEffects ?? []).filter(Boolean))]
}

function dormantNpcEffects(npc: NPC) {
  return [...new Set((npc?.dormantEffects ?? []).filter(Boolean))]
}

function buildNpcSheet(npc: NPC) {
  const relationship = Number.isFinite(npc?.relationship) ? Number(npc.relationship) : 0
  return normalizeNpcSheet({
    identity: String(npc?.identity ?? '').trim(),
    lore: String(npc?.lore ?? '').trim(),
    sex: String(npc?.sex ?? '').trim(),
    relationship,
    relationshipLabel: relationLabel(relationship),
    relationshipTone: relationTone(relationship),
    intentions: npc?.intentions ?? [],
    memories: npc?.memory ?? [],
    inventory: npc?.inventory ?? [],
    activeEffects: activeNpcEffects(npc),
    dormantEffects: dormantNpcEffects(npc),
  })
}

export function buildInfoDialog(config: InfoDialogConfig = {}): GameDialogOptions {
  return {
    content: 'info-dialog',
    data: config.data ?? {},
    options: config.options ?? {},
  }
}

export function openInfoDialog(origin: string, config: InfoDialogConfig = {}) {
  dispatcher.trigger(origin, TOPICS.ui.dialogOpen, buildInfoDialog(config))
}

export async function openItemDialog(origin: string, itemName: string, config: ItemDialogConfig = {}) {
  const gs = gameStore.state
  const inventoryEntry = gs?.player.inventory.find(entry => entry.name === itemName)
  const roomEntry = gs?.currentRoom.items.find(i => typeof i === 'object' && (i as any).name === itemName) as any
  
  const location = config.location ?? (inventoryEntry ? 'inventory' : 'room')
  const equipped = config.equipped ?? Boolean(inventoryEntry?.equipped)
  const worth = inventoryEntry?.worth ?? roomEntry?.worth ?? 0
  
  const vessel = gs?.player
  const actions = vessel
    ? await getItemActions(vessel, itemName, location, equipped, {
        gs,
        item: inventoryEntry ?? roomEntry ?? null,
      })
    : [{ label: 'EXAMINE', input: `Examine ${itemName}` }]
  const statusInfo = getItemStatusInfo(itemName, location, equipped)

  const econ = gs?.world.taxonomy?.economy
  const worthLabel = worth && econ ? `${worth} ${econ.currencySymbol}` : ''

  openInfoDialog(origin, {
    data: {
      media: {
        imageKind: 'item',
        imageSeed: itemName,
        fallbackLabel: itemName,
      },
    },
    options: {
      title: decorateObjectName(itemName),
      desc: config.desc ?? statusInfo.detail,
      meta: [
        location === 'inventory' ? 'INVENTORY ITEM' : 'ROOM ITEM',
        worthLabel
      ].filter(Boolean).join(' · '),
      actions,
    },
  })
}

export async function openNpcDialog(origin: string, npc: NPC) {
  const vessel = gameStore.state?.player
  const actions = vessel
    ? await getNpcActions(vessel, npc)
    : [
        { label: 'TALK TO', input: `Talk to ${npc.name}` },
        { label: 'EXAMINE', input: `Examine ${npc.name}` },
      ]

  dispatcher.trigger(origin, TOPICS.ui.dialogOpen, {
    content: 'npc-sheet-dialog',
    data: {
      sheet: buildNpcSheet(npc),
      effectChipText,
      imageKind: 'npc',
      imageSeed: `${npc.name} — ${npc.identity}. ${npc.lore ?? ''}`.trim(),
      fallbackLabel: npc.name,
      onOpenItem: (item: string) => openItemDialog(origin, item, {
        desc: 'Known NPC item.',
        meta: 'KNOWN NPC ITEM',
      }),
    },
    options: {
      title: npc.name,
      actions,
    },
  })
}
